import React, { useMemo } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { getThemeById } from '../../config/themes'
import type { TenantThemeConfig } from '../../types/theme'
import './historyLayout.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

export interface HistoryOutletContext {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
  getAssetUrl: (path: string) => string
}

interface ModernHistoryLayoutProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

type HistoryTab = {
  to: string
  fa: string
  tone: string
  sub: string
  label: string
}

const TABS: HistoryTab[] = [
  { to: 'deposit', fa: 'fas fa-wallet', tone: 'deposit', sub: 'ประวัติ', label: 'การฝากเงิน' },
  { to: 'withdraw', fa: 'fas fa-money-bill-wave', tone: 'withdraw', sub: 'ประวัติ', label: 'การถอนเงิน' },
  { to: 'credit', fa: 'fas fa-coins', tone: 'credit', sub: 'ประวัติ', label: 'การรับเครดิต' },
  { to: 'gamble', fa: 'fas fa-dice', tone: 'gamble', sub: 'ประวัติ', label: 'การเล่นพนัน' },
  { to: 'pending', fa: 'fas fa-hourglass-half', tone: 'pending', sub: 'รอผล', label: 'การเล่นพนัน' },
  { to: 'games', fa: 'fas fa-gamepad', tone: 'games', sub: 'ประวัติ', label: 'เกมส์' },
]

/** Layout หน้า history: แท็บหลัก + Outlet (เกมส์แยกเป็นซับแท็บใน /history/games) */
const ModernHistoryLayout: React.FC<ModernHistoryLayoutProps> = ({ themeConfig, assetConfig }) => {
  const location = useLocation()
  const themeFolder = useMemo(() => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue', [themeConfig.themeId])
  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }
  const context: HistoryOutletContext = { themeConfig, assetConfig, getAssetUrl }

  return (
    <div className="content htd-hist">
      <div className="htd-hist__glow" aria-hidden />
      <div className="htd-hist__inner">
        <header className="htd-hist__hero htd-hist-anim htd-hist-anim--1">
          <div className="htd-hist__badge" aria-hidden>
            <i className="fas fa-history" />
          </div>
          <div>
            <p className="htd-hist__eyebrow">Transaction History</p>
            <h1 className="htd-hist__title">ประวัติรายการ</h1>
            <p className="htd-hist__desc">ติดตามฝาก ถอน เครดิต และประวัติการเล่นได้จากที่เดียว</p>
          </div>
        </header>

        <nav className="htd-hist__tabs htd-hist-anim htd-hist-anim--2" aria-label="แท็บประวัติ">
          {TABS.map((tab, index) => {
            const isGames = tab.to === 'games'
            const gamesActive = isGames && location.pathname.includes('/history/games')
            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.to === 'deposit'}
                className={({ isActive }) =>
                  `htd-hist__tab htd-hist__tab--${tab.tone}${isActive || gamesActive ? ' is-active' : ''}`
                }
                style={{ animationDelay: `${0.06 + index * 0.04}s` }}
              >
                <span className="htd-hist__tab-icon" aria-hidden>
                  <i className={tab.fa} />
                </span>
                <span className="text-center">
                  <span className="htd-hist__tab-sub">{tab.sub}</span>
                  <span className="htd-hist__tab-label">{tab.label}</span>
                </span>
              </NavLink>
            )
          })}
        </nav>

        <div className="htd-hist__outlet htd-hist-anim htd-hist-anim--3">
          <Outlet context={context} />
        </div>
      </div>
    </div>
  )
}

export default ModernHistoryLayout
